import {
	Component,
	Element,
	h,
	Host,
	Method,
	Prop,
	State,
} from '@stencil/core';
import { TabTransitionType } from '../../_types/string-literals';
import { Timer, TimerFactory } from '../../../../common/_helpers/timer';

@Component({
	tag: 'ds-tab',
	shadow: false,
})
export class Tab {
	/* Identifier for the tab within its tab group. Set by the tab group on initialisation. */
	@Prop({ reflect: true }) index: string;

	/* Label text to show inside the tab button for this tab.
	 * Note: To show a "fallback label", add `<ds-fallback-tab-label>TAB LABEL</ds-fallback-tab-label>` inside the tab HTML
	 *       The fallback label is what is shown when the breakpoint is between xs and md, and also when JS is disabled or
	 *       there is a JS error such as a polyfill clash in IE
	 **/
	@Prop({ mutable: true, attribute: 'label' }) label: string;

	/**
	 * A dcr field for the publishers to input a more suitable location hash string.
	 * As an optional field, when empty, the required field label will be used instead.
	 */
	@Prop({ attribute: 'legend', mutable: true }) legend = '';

	/* Whether or not to show the label (for example, hide the label if there's only one tab)
	 */
	@Prop({ reflect: true, mutable: true }) showLabel = true;

	/* Whether or the tab is selected and therefore visible
	 */
	@Prop({ reflect: true, mutable: true }) selected = false;

	@Prop({ mutable: true }) icon = '';

	/* Affects the style of animation when a tab is selected or de-selected
	 */
	@Prop() transitionType: TabTransitionType;

	@Element() el: HTMLDsTabElement;

	/* The visibility of the current tab which could be in the middle of a transition. Allows specific CSS transitions.
	 */
	@State() visible = false;
	@State() fadingIn = false;
	@State() fadingOut = false;

	fadeInDuration = 500;
	fadeOutDuration = 500;
	transitionTimer: Timer;
	// Generates unique id for tab
	tabUniqueId = (Math.random() + 1).toString(36).substring(7).replace(/ /g, '');

	@Method() async hide(): Promise<void> {
		if (this.transitionTimer) {
			this.transitionTimer.abort();
		}
		this.fadingIn = false;
		this.fadingOut = false;
		this.visible = false;
	}

	@Method() async show(): Promise<void> {
		if (this.transitionTimer) {
			this.transitionTimer.abort();
		}
		this.fadingIn = false;
		this.fadingOut = false;
		this.visible = true;
	}

	@Method() async fadeIn(): Promise<void> {
		if (this.transitionTimer) {
			this.transitionTimer.abort();
		}
		this.fadingOut = false;
		this.fadingIn = true;
		const microDelay = TimerFactory(50);
		await microDelay.tickTock();
		this.visible = true;
		this.transitionTimer = TimerFactory(this.fadeInDuration);
		await this.transitionTimer.tickTock();
		this.fadingIn = false;
	}

	@Method() async fadeOut(): Promise<void> {
		if (this.transitionTimer) {
			this.transitionTimer.abort();
		}
		this.fadingIn = false;
		this.fadingOut = true;
		const microDelay = TimerFactory(50);
		await microDelay.tickTock();
		this.visible = false;
		this.transitionTimer = TimerFactory(this.fadeOutDuration);
		await this.transitionTimer.tickTock();
		this.fadingOut = false;
	}

	@Method() async measureHeight(): Promise<number> {
		this.el.style.visibility = 'hidden';
		this.el.style.display = 'block';
		const height = this.el.clientHeight;
		this.el.style.removeProperty('visibility');
		this.el.style.removeProperty('display');
		return height;
	}

	componentWillLoad(): void {
		if (!this.showLabel) {
			const fallbackLabel = this.el.querySelector<HTMLElement>(
				'ds-fallback-tab-label'
			);
			if (fallbackLabel) {
				fallbackLabel.classList.add('d-none');
			}
		}
	}

	render() {
		return (
			<Host
				id={this.tabUniqueId}
				class={{
					'ds-tab-fading-in': this.fadingIn,
					'ds-tab-fading-out': this.fadingOut,
					'ds-tab-visible': this.visible,
				}}
				role="tabpanel"
				aria-hidden={this.visible ? 'false' : 'true'}
				aria-label={this.label}
			>
				<slot></slot>
			</Host>
		);
	}
}
